import React, { useEffect, useState } from 'react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgTv from '@icons/tv.svg?react';
import { ModeEnum, TvDashboardSettingsDTO } from '@api/ringil-api.v2.tsx';
import TvDashboardSettingsEditor, { useTvFieldLabels } from '@components/ringil3/Features/Location/TvDashboardSettingsEditor.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  value: TvDashboardSettingsDTO;
  maxColumns: number;
  onChange: (value: TvDashboardSettingsDTO) => void;
  readOnly?: boolean;
  dataTestId?: string;
}

const LocationTvDashboardTag: React.FC<ComponentProps> = ({ secured, value, maxColumns, onChange, readOnly, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const fields = useTvFieldLabels();
  const [internalValue, setInternalValue] = useState<TvDashboardSettingsDTO>(value);

  useEffect(() => setInternalValue(value), [value]);

  const shownNames = fields.filter(f => value[f.key]?.mode !== ModeEnum.HIDDEN).map(f => f.label);
  const displayValue =
    shownNames.length > 0
      ? tr('LocalityDetail.tvDashboardValue', '{{cnt}} per ramp · {{fields}}', { cnt: value.columnsCount, fields: shownNames.join(' | ') })
      : tr('LocalityDetail.tvDashboardNotSet', 'Not set');

  return (
    <ButtonTag
      secured={secured}
      label={tr('LocalityDetail.tvDashboard', 'Internal TV dashboard')}
      icon={<SvgTv className="w-4 h-4" />}
      value={displayValue}
      actionIcon={<SvgTagEdit />}
      actionPane={
        <TvDashboardSettingsEditor
          value={internalValue}
          maxColumns={maxColumns}
          onChange={setInternalValue}
          dataTestId={dataTestId}
        />
      }
      popupWidth={'520px'}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      readonly={readOnly}
      autoClose
      dataTestId={dataTestId}
      dataTestIdValue={dataTestId ? `${dataTestId}Value` : undefined}
    />
  );
};

export default LocationTvDashboardTag;
